import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import {
  ICatalogueScreenSetting,
  IEBasketMgmtNewSettings,
  IGlideDisablePromotionSetting,
  IGlideMgmtSettings,
  IProductRecommendationSetting,
  IRealTimeStockAvailabilitySetting,
  ISplitPaymentsSetting
} from '@shared/interfaces';
import { GlideSettingsService, ICatalogueIdName, IStoreHierarchy } from './glide-settings.service';

const CATALOGUE_CONFIG_PREFIX = 'CATALOGUE_CONFIG::';
const PRODUCT_REC_CONFIG_PREFIX = 'PRODUCT_REC_CONFIG::';
const NODE_PREFIX = 'NODE::';
// GE-4848. Whole numbers greater than zero - rejects decimals and 0, same as eBasket.
const RETRY_LIMIT_PATTERN = '^[1-9][0-9]*$';
// Key under which this section registers itself on the parent module-assignment form,
// so the mandatory dropdown blocks Save until a selection is made (GE-3630, scenario 2).
const CATALOGUE_SCREEN_CONTROL = 'catalogueScreenSettings';

@Component({
  selector: 'app-module-assignment-glide-settings',
  templateUrl: './glide-settings.component.html',
  styleUrl: './glide-settings.component.scss'
})
export class GlideSettingsComponent implements OnInit, OnChanges, OnDestroy {
  hostName: any;

  @Input() isCompanyLevel!: boolean;
  // Product Recommendation is shown for all node levels except organization level.
  @Input() showProductRecommendation!: boolean;
  // Catalogue Screen is shown for all node levels except organization level (GE-4466);
  // previously it only rendered for isCompanyLevel, which hid it on every child node.
  @Input() showCatalogueScreen!: boolean;
  // Every node level except organization, as with the two settings above (GE-4543).
  @Input() showRealTimeStockAvailability!: boolean;
  // Every node level except organization (GE-5004). Lives in the Payment Settings panel
  // rather than Product Management, but shares this component's single form group.
  @Input() showSplitPayments!: boolean;
  // Organisation level and every node level (GE-4848). Unlike Split Payments, this one is
  // configurable at organisation too.
  @Input() showVoidAfterPartPayment!: boolean;
  // GE-5258. Organisation level and every node level, with inheritance like eBasket's own
  // Disable Promotion toggle.
  @Input() showDisablePromotion!: boolean;
  // A store node always includes its own store, so that checkbox is forced on and locked.
  // currentNodeId is the node being edited, not its company scope id.
  @Input() isStoreLevel!: boolean;
  @Input() currentNodeId?: string;
  @Input() companyId!: string;
  @Input() parentForm?: UntypedFormGroup;
  @Input() eBasketMgmtSettings?: IEBasketMgmtNewSettings[];
  @Input() glideMgmtSettings?: IGlideMgmtSettings;

  productManagementForm!: FormGroup;
  catalogueConfigOptions: ICatalogueIdName[] = [];
  productRecommendationOptions: ICatalogueIdName[] = [];
  storeHierarchy: IStoreHierarchy = { companyName: '', tradingRegions: [] };

  constructor(private glideSettingsService: GlideSettingsService) {}

  ngOnInit() {
    this.hostName = window.location.origin;
    this.productManagementForm = new FormGroup({
      // Disabled until the company has at least one catalogue config to choose from.
      catalogueScreenEnabled: new FormControl({ value: false, disabled: true }),
      catalogueConfig: new FormControl(null),
      // Disabled until there is at least one recommendation config to choose from.
      productRecommendationEnabled: new FormControl({ value: false, disabled: true }),
      productRecommendationConfig: new FormControl(null),
      productRecommendationMessage: new FormControl(null),
      // Off at every node until explicitly configured.
      realTimeStockAvailabilityEnabled: new FormControl(false),
      // One control holding an array, not a FormArray: the store list arrives async. The
      // checkboxes read and write through the helpers below rather than formControlName.
      realTimeStockStoreIds: new FormControl<string[]>([]),
      // Off at every node until explicitly configured. No dependent field, so it carries
      // no validators and can never block Save.
      splitPaymentsEnabled: new FormControl(false),
      // GE-4848. Off until configured. The retry limit always rejects decimals and zero;
      // it only becomes mandatory while the toggle is on - see syncRetryLimitValidation.
      voidTransactionEnabled: new FormControl(false),
      paymentFailureRetryLimit: new FormControl('', [Validators.pattern(RETRY_LIMIT_PATTERN)]),
      // GE-5258. Off by default, so promotions apply on Glide until switched on.
      disablePromotionEnabled: new FormControl(false)
    });

    // Register on the parent form so the mandatory fields disable Save (scenario 2),
    // whenever any section is shown.
    if (
      this.parentForm &&
      (this.isCompanyLevel ||
        this.showProductRecommendation ||
        this.showCatalogueScreen ||
        this.showRealTimeStockAvailability ||
        this.showSplitPayments ||
        this.showVoidAfterPartPayment ||
        this.showDisablePromotion)
    ) {
      this.parentForm.setControl(CATALOGUE_SCREEN_CONTROL, this.productManagementForm, {
        emitEvent: false
      });
    }

    this.updateProductRecommendationAvailability();
    this.loadCatalogueScreenSetting();
    this.loadProductRecommendationSetting();
    this.loadRealTimeStockAvailabilitySetting();
    this.loadSplitPaymentsSetting();
    this.loadVoidAfterPartPaymentSetting();
    this.loadDisablePromotionSetting();
  }

  ngOnDestroy() {
    if (this.parentForm?.get(CATALOGUE_SCREEN_CONTROL)) {
      this.parentForm.removeControl(CATALOGUE_SCREEN_CONTROL, { emitEvent: false });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['companyId'] || changes['isCompanyLevel'] || changes['showCatalogueScreen']) &&
      (this.isCompanyLevel || this.showCatalogueScreen) &&
      this.companyId
    ) {
      this.loadCatalogueConfigs();
    }
    if (
      (changes['companyId'] || changes['showProductRecommendation']) &&
      this.showProductRecommendation &&
      this.companyId
    ) {
      this.loadProductRecommendations();
    }
    if (changes['eBasketMgmtSettings']) {
      this.loadCatalogueScreenSetting();
      this.loadProductRecommendationSetting();
    }
    if (
      (changes['companyId'] || changes['showRealTimeStockAvailability']) &&
      this.showRealTimeStockAvailability &&
      this.companyId
    ) {
      this.loadCompanyStoreHierarchy();
    }
    if (changes['glideMgmtSettings']) {
      this.loadRealTimeStockAvailabilitySetting();
      this.loadSplitPaymentsSetting();
      this.loadVoidAfterPartPaymentSetting();
      this.loadDisablePromotionSetting();
    }
    if (changes['isStoreLevel'] || changes['currentNodeId']) {
      this.applyForcedStoreSelection();
      this.syncRealTimeStockValidation();
    }
  }

  private loadCatalogueConfigs() {
    this.glideSettingsService.getCatalogueIdNameList(this.companyId).subscribe({
      next: (list) => {
        this.catalogueConfigOptions = list ?? [];
        this.updateToggleAvailability();
      },
      error: () => {
        this.catalogueConfigOptions = [];
        this.updateToggleAvailability();
      }
    });
  }

  private loadProductRecommendations() {
    this.glideSettingsService.getProductRecommendationList(this.companyId).subscribe({
      next: (list) => {
        this.productRecommendationOptions = list ?? [];
        this.updateProductRecommendationAvailability();
      },
      error: () => {
        this.productRecommendationOptions = [];
        this.updateProductRecommendationAvailability();
      }
    });
  }

  private loadCompanyStoreHierarchy() {
    this.glideSettingsService.getCompanyStoreHierarchy(this.companyId).subscribe({
      next: (hierarchy) => {
        this.storeHierarchy = hierarchy ?? { companyName: '', tradingRegions: [] };
        // Hierarchy and saved selection arrive independently; whichever lands second reconciles.
        this.applyForcedStoreSelection();
        this.syncRealTimeStockValidation();
      },
      error: () => {
        this.storeHierarchy = { companyName: '', tradingRegions: [] };
        this.applyForcedStoreSelection();
        this.syncRealTimeStockValidation();
      }
    });
  }

  private loadRealTimeStockAvailabilitySetting() {
    if (!this.productManagementForm) {
      return;
    }
    const setting = this.glideMgmtSettings?.realTimeStockAvailability;

    this.productManagementForm
      .get('realTimeStockAvailabilityEnabled')
      ?.setValue(!!setting?.status, { emitEvent: false });
    // Stored ids and option ids are both already NODE::-prefixed, so no strip/re-apply.
    // Normalised because the stored array is untyped at runtime: a non-string entry
    // (an object from an older save, or a stray flag) would reach withNodePrefix and
    // throw "startsWith is not a function" on the next save.
    this.productManagementForm
      .get('realTimeStockStoreIds')
      ?.setValue(this.toStoreIdList(setting?.storeNodeIds), { emitEvent: false });

    this.applyForcedStoreSelection();
    this.syncRealTimeStockValidation();
  }

  // GE-5004. The backend returns this already resolved against the node's ancestors, so
  // there is no cascade to apply here - render exactly what came back.
  private loadSplitPaymentsSetting() {
    if (!this.productManagementForm) {
      return;
    }
    const setting = this.glideMgmtSettings?.paymentSettings?.splitPayments;

    this.productManagementForm
      .get('splitPaymentsEnabled')
      ?.setValue(!!setting?.status, { emitEvent: false });
  }

  // GE-4848. Backend returns this already resolved against the node's ancestors, so a node
  // with no override of its own simply renders the organisation's value.
  private loadVoidAfterPartPaymentSetting() {
    if (!this.productManagementForm) {
      return;
    }
    const settings = this.glideMgmtSettings?.paymentSettings;

    this.productManagementForm
      .get('voidTransactionEnabled')
      ?.setValue(!!settings?.voidTransaction, { emitEvent: false });
    this.productManagementForm
      .get('paymentFailureRetryLimit')
      ?.setValue(settings?.paymentFailureRetryLimit ?? '', { emitEvent: false });

    this.syncRetryLimitValidation();
  }

  // GE-5258. Already resolved against the node's ancestors by the backend, so a node with no
  // override renders the organisation's value. A missing value is off.
  private loadDisablePromotionSetting() {
    if (!this.productManagementForm) {
      return;
    }
    this.productManagementForm
      .get('disablePromotionEnabled')
      ?.setValue(!!this.glideMgmtSettings?.disablePromotion?.status, { emitEvent: false });
  }

  toggleVoidAfterPartPayment() {
    this.syncRetryLimitValidation();
  }

  // Mirrors eBasket's toggleVoidTransaction: mandatory only while the toggle is on, and
  // cleared when it is switched off so nothing invalid is persisted.
  private syncRetryLimitValidation() {
    const control = this.productManagementForm?.get('paymentFailureRetryLimit');
    if (!control) {
      return;
    }
    if (this.productManagementForm.get('voidTransactionEnabled')?.value) {
      control.setValidators([Validators.required, Validators.pattern(RETRY_LIMIT_PATTERN)]);
    } else {
      control.setValidators([Validators.pattern(RETRY_LIMIT_PATTERN)]);
      control.setValue('');
    }
    control.updateValueAndValidity();
  }

  private loadCatalogueScreenSetting() {
    if (!this.productManagementForm) {
      return;
    }
    const setting = this.eBasketMgmtSettings?.[0]?.catalogueScreen;
    const enabled = !!setting?.enabled;

    this.productManagementForm
      .get('catalogueScreenEnabled')
      ?.setValue(enabled, { emitEvent: false });
    this.productManagementForm
      .get('catalogueConfig')
      ?.setValue(enabled ? this.withoutCatalogueConfigPrefix(setting?.configId) : null, {
        emitEvent: false
      });
    this.syncMandatoryDropdown();
  }

  private loadProductRecommendationSetting() {
    if (!this.productManagementForm) {
      return;
    }
    const setting = this.eBasketMgmtSettings?.[0]?.productRecommendation;
    const enabled = !!setting?.enabled;

    this.productManagementForm
      .get('productRecommendationEnabled')
      ?.setValue(enabled, { emitEvent: false });
    this.productManagementForm
      .get('productRecommendationConfig')
      ?.setValue(enabled ? this.withoutProductRecConfigPrefix(setting?.configId) : null, {
        emitEvent: false
      });
    this.productManagementForm
      .get('productRecommendationMessage')
      ?.setValue(enabled ? setting?.customMessaging ?? null : null, { emitEvent: false });
    this.toggleProductRecommendation();
  }

  // No catalogue configs for the company → the feature can't be enabled, so keep the
  // toggle disabled (and off). Enable it once configs are available.
  private updateToggleAvailability() {
    const toggle = this.productManagementForm?.get('catalogueScreenEnabled');
    if (!toggle) {
      return;
    }
    if (this.catalogueConfigOptions.length) {
      toggle.enable({ emitEvent: false });
    } else {
      toggle.setValue(false, { emitEvent: false });
      toggle.disable({ emitEvent: false });
      this.syncMandatoryDropdown();
    }
  }

  toggleCatalogueScreen() {
    this.syncMandatoryDropdown();
  }

  private updateProductRecommendationAvailability() {
    const toggle = this.productManagementForm?.get('productRecommendationEnabled');
    if (!toggle) {
      return;
    }
    if (this.productRecommendationOptions.length) {
      toggle.enable({ emitEvent: false });
    } else {
      toggle.setValue(false, { emitEvent: false });
      toggle.disable({ emitEvent: false });
      this.toggleProductRecommendation();
    }
  }

  toggleProductRecommendation() {
    const config = this.productManagementForm.get('productRecommendationConfig');
    const message = this.productManagementForm.get('productRecommendationMessage');
    if (this.productManagementForm.get('productRecommendationEnabled')?.value) {
      // Enabling makes both the dropdown and the custom message mandatory.
      config?.setValidators([Validators.required]);
      message?.setValidators([Validators.required, Validators.maxLength(100)]);
    } else {
      // Disabling clears both so nothing is applied.
      config?.clearValidators();
      config?.setValue(null);
      message?.clearValidators();
      message?.setValue(null);
    }
    config?.updateValueAndValidity();
    message?.updateValueAndValidity();
  }

  toggleRealTimeStockAvailability() {
    this.applyForcedStoreSelection();
    this.syncRealTimeStockValidation();
  }

  // Deliberately keeps its selection when off, unlike the two settings above: off means fall
  // back to inherited, which is meaningless if the choice is discarded. Do not "fix" this.
  private syncRealTimeStockValidation() {
    const storeIds = this.productManagementForm?.get('realTimeStockStoreIds');
    if (!storeIds) {
      return;
    }
    if (this.productManagementForm.get('realTimeStockAvailabilityEnabled')?.value) {
      // required treats an empty array as empty - the minimum-one-store rule.
      storeIds.setValidators([Validators.required]);
    } else {
      storeIds.clearValidators();
    }
    storeIds.updateValueAndValidity({ emitEvent: false });
  }

  private applyForcedStoreSelection() {
    const control = this.productManagementForm?.get('realTimeStockStoreIds');
    const forcedId = this.forcedStoreId;
    if (!control || !forcedId) {
      return;
    }
    const selected: string[] = control.value ?? [];
    if (!selected.includes(forcedId)) {
      control.setValue([...selected, forcedId], { emitEvent: false });
    }
  }

  // Public: bound as [lockedStoreId] on the hierarchy selector.
  get forcedStoreId(): string | null {
    if (!this.isStoreLevel || !this.currentNodeId) {
      return null;
    }
    return this.withNodePrefix(this.currentNodeId);
  }

  onStoreSelectionChange(storeIds: string[]) {
    const control = this.productManagementForm?.get('realTimeStockStoreIds');
    if (!control) {
      return;
    }
    control.setValue([...storeIds], { emitEvent: false });
    control.markAsTouched();
    this.applyForcedStoreSelection();
    this.syncRealTimeStockValidation();
  }

  get realTimeStockStoreIdsControl() {
    return this.productManagementForm?.get('realTimeStockStoreIds');
  }

  // Bound directly in the Payment Settings panel, which sits outside the [formGroup] div.
  get splitPaymentsControl(): FormControl {
    return this.productManagementForm?.get('splitPaymentsEnabled') as FormControl;
  }

  get voidTransactionControl(): FormControl {
    return this.productManagementForm?.get('voidTransactionEnabled') as FormControl;
  }

  get paymentFailureRetryLimitControl(): FormControl {
    return this.productManagementForm?.get('paymentFailureRetryLimit') as FormControl;
  }

  getRealTimeStockAvailabilitySetting(): IRealTimeStockAvailabilitySetting {
    const status = !!this.productManagementForm?.get('realTimeStockAvailabilityEnabled')?.value;
    const storeNodeIds = this.toStoreIdList(
      this.productManagementForm?.get('realTimeStockStoreIds')?.value
    );
    return {
      status,
      // Emitted whatever the toggle says - see syncRealTimeStockValidation.
      storeNodeIds: storeNodeIds.map((id) => this.withNodePrefix(id)).filter((id) => !!id)
    };
  }

  getSplitPaymentsSetting(): ISplitPaymentsSetting {
    return {
      status: !!this.productManagementForm?.get('splitPaymentsEnabled')?.value
    };
  }

  getDisablePromotionSetting(): IGlideDisablePromotionSetting {
    return {
      status: !!this.productManagementForm?.get('disablePromotionEnabled')?.value
    };
  }

  getVoidTransactionSetting(): boolean {
    return !!this.productManagementForm?.get('voidTransactionEnabled')?.value;
  }

  getPaymentFailureRetryLimitSetting(): number {
    return Number(this.productManagementForm?.get('paymentFailureRetryLimit')?.value) || 0;
  }

  // Accepts the raw stored value, which is only typed as string[] at compile time. Keeps
  // string ids, unwraps the { id } option shape, and repairs the character-indexed maps
  // ({"0":"N","1":"O",...}) that node-management-api wrote before GE-4944 fixed its diff
  // helper. Anything else is dropped rather than becoming a bogus "NODE::[object Object]".
  private toStoreIdList(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .map((entry: any) => {
        if (typeof entry === 'string') {
          return entry;
        }
        if (typeof entry?.id === 'string') {
          return entry.id;
        }
        return this.fromCharIndexedMap(entry);
      })
      .filter((id: any): id is string => typeof id === 'string' && id.length > 0);
  }

  // Rebuilds a string that was persisted as { "0": "N", "1": "O", ... } by reading its
  // numeric keys in order. Returns null for anything that is not such a map.
  private fromCharIndexedMap(entry: any): string | null {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null;
    }
    const keys = Object.keys(entry);
    if (!keys.length || !keys.every((key) => /^\d+$/.test(key))) {
      return null;
    }
    const rebuilt = keys
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => entry[key])
      .join('');
    return rebuilt.length ? rebuilt : null;
  }

  private withNodePrefix(nodeId: string): string {
    // Guarded on type, not just presence: a non-string here is what threw
    // "startsWith is not a function", and `?.` alone would turn null into "NODE::null".
    if (typeof nodeId !== 'string' || !nodeId.length) {
      return '';
    }
    return nodeId.startsWith(NODE_PREFIX) ? nodeId : `${NODE_PREFIX}${nodeId}`;
  }

  private syncMandatoryDropdown() {
    const catalogueConfig = this.productManagementForm.get('catalogueConfig');
    if (!catalogueConfig) {
      return;
    }
    if (this.productManagementForm.get('catalogueScreenEnabled')?.value) {
      // Enabling the toggle makes the dropdown mandatory (blank by default).
      catalogueConfig.setValidators([Validators.required]);
    } else {
      // Disabling clears the selection so no configuration is applied.
      catalogueConfig.clearValidators();
      catalogueConfig.setValue(null);
    }
    catalogueConfig.updateValueAndValidity();
  }

  getCatalogueScreenSetting(): ICatalogueScreenSetting {
    const enabled = !!this.productManagementForm?.get('catalogueScreenEnabled')?.value;
    const selectedId = this.productManagementForm?.get('catalogueConfig')?.value;
    return {
      enabled,
      configId: enabled && selectedId ? this.withCatalogueConfigPrefix(selectedId) : ''
    };
  }

  private withCatalogueConfigPrefix(configId: string): string {
    return configId?.startsWith(CATALOGUE_CONFIG_PREFIX)
      ? configId
      : `${CATALOGUE_CONFIG_PREFIX}${configId}`;
  }

  getProductRecommendationSetting(): IProductRecommendationSetting {
    const enabled = !!this.productManagementForm?.get('productRecommendationEnabled')?.value;
    const selectedId = this.productManagementForm?.get('productRecommendationConfig')?.value;
    const message = this.productManagementForm?.get('productRecommendationMessage')?.value;
    return {
      enabled,
      configId: enabled && selectedId ? this.withProductRecConfigPrefix(selectedId) : '',
      customMessaging: enabled ? message ?? '' : ''
    };
  }

  private withProductRecConfigPrefix(configId: string): string {
    return configId?.startsWith(PRODUCT_REC_CONFIG_PREFIX)
      ? configId
      : `${PRODUCT_REC_CONFIG_PREFIX}${configId}`;
  }

  private withoutProductRecConfigPrefix(configId?: string): string | null {
    if (!configId) {
      return null;
    }
    return configId.startsWith(PRODUCT_REC_CONFIG_PREFIX)
      ? configId.slice(PRODUCT_REC_CONFIG_PREFIX.length)
      : configId;
  }

  private withoutCatalogueConfigPrefix(configId?: string): string | null {
    if (!configId) {
      return null;
    }
    return configId.startsWith(CATALOGUE_CONFIG_PREFIX)
      ? configId.slice(CATALOGUE_CONFIG_PREFIX.length)
      : configId;
  }
}
